import type { ComponentType } from 'react';
import * as I from '@/icons/Icon';
import { ACCESS, type Access } from '@/features/access/access';
import { focusCommandTarget } from './command-palette-focus';
import type { CommandNavigate } from './command-palette-registry';

/**
 * The palette's quick actions, as data.
 *
 * Non-component module so `QuickActions.tsx` stays a Vite Fast Refresh
 * boundary — same rule (and same reason) as `section-meta.ts`. `quickActionsCount`
 * living in the `.tsx` previously dragged `CommandPalette.tsx`, which imports
 * it, into the same full-reload HMR graph.
 */

/**
 * Everything a quick action needs to decide whether it can run.
 *
 * Each field is sourced from the SAME hook the real control uses, never
 * re-derived — see `use-quick-action-gates.ts`. A palette action that offers
 * something the page will refuse is worse than one that explains itself:
 * `focusCommandTarget` polls 60 frames for an input that never mounts and
 * then gives up silently, so the user gets a dead click with no reason.
 */
export type QuickActionContext = {
  /**
   * Role-map check (`useRoleCan().can`). A role-blocked action is HIDDEN, never
   * disabled — see `visibleQuickActions`.
   */
  can: (access?: Access | null) => boolean;
  /** Workspace admin/owner row — the invite card's own gate, ANDed with the role map. */
  canManageTeam: boolean;
  /** The org's plan doesn't grant `settings/team-invite`. */
  teamInviteLocked: boolean;
  /** Copy for the plan-locked invite case, e.g. "Unlocks on the Growth plan." */
  teamInviteLockedReason: string | null;
  /** Non-null when the subscription is PAUSED — outranks every role reason. */
  pausedReason: string | null;
  /** Any gate above is still in flight; render neither enabled nor refused. */
  isResolving: boolean;
};

export type SimpleAction = {
  id: string;
  label: string;
  /** Extra search terms, so "add workspace" or "new member" finds the row
   * even though the label says "Create" / "Invite". Write them as whole
   * phrases: cmdk scores the query's characters IN ORDER across
   * `label + keywords`, so a bare `'add'` keyword matches "add" but never
   * "add workspace" (no "workspace" follows it). */
  keywords: string[];
  icon: ComponentType<{ size?: number }>;
  /** The (Feature, Op) the action performs; role-blocked → not offered at all. */
  access: Access;
  /** Extra non-role visibility (per-workspace membership). */
  visible?: (ctx: QuickActionContext) => boolean;
  /**
   * Non-null → the row renders disabled with this copy as its tooltip.
   * Mirrors the real control's own gate: paused, then plan lock. Role is not
   * here — a role-blocked action never reaches this point.
   */
  disabledReason: (ctx: QuickActionContext) => string | null;
  run: (navigate: CommandNavigate) => void;
};

/**
 * "Add email account" / "Invite teammate" / "Create workspace" are all the
 * same shape — navigate, then (for the two inline-card flows) focus the
 * existing form's input rather than opening a new dialog, per the confirmed
 * product decision. One row renderer covers all three instead of three
 * near-identical `CommandItem`s.
 */
export const SIMPLE_ACTIONS: SimpleAction[] = [
  {
    id: 'add-email-account',
    label: 'Add email account',
    keywords: ['connect email account', 'create email account', 'new email account', 'add mailbox', 'connect mailbox', 'new mailbox', 'add inbox'],
    icon: I.Mail,
    access: ACCESS.connectAccount,
    disabledReason: (ctx) => ctx.pausedReason,
    run: (navigate) => void navigate({ to: '/email-accounts/connect' }),
  },
  {
    id: 'invite-teammate',
    label: 'Invite teammate',
    keywords: ['add teammate', 'add member', 'new member', 'add user', 'invite member', 'invite user', 'team', 'people'],
    icon: I.Users,
    access: ACCESS.teamManage,
    visible: (ctx) => ctx.canManageTeam,
    disabledReason: (ctx) =>
      ctx.pausedReason ?? (ctx.teamInviteLocked ? ctx.teamInviteLockedReason : null),
    run: (navigate) => {
      void navigate({ to: '/settings', search: { section: 'access' } });
      focusCommandTarget('invite-email');
    },
  },
  {
    id: 'create-workspace',
    label: 'Create workspace',
    keywords: ['add workspace', 'new workspace'],
    icon: I.Layers,
    access: ACCESS.workspaceCreate,
    disabledReason: (ctx) => ctx.pausedReason,
    run: (navigate) => {
      void navigate({ to: '/settings', search: { section: 'workspaces' } });
      focusCommandTarget('new-workspace-name');
    },
  },
];

/** Synonyms for the "Create API key" row (rendered outside `SIMPLE_ACTIONS`
 * because it has its own gate hook). */
export const CREATE_API_KEY_KEYWORDS = [
  'add api key',
  'new api key',
  'generate api key',
  'api token',
  'api-keys',
  '/api-keys',
];

/**
 * The simple actions the caller's role may use. Unresolved gates offer nothing
 * gated (`can` is false until the profile lands), so a role-blocked row never
 * flashes in.
 */
export function visibleQuickActions(ctx: QuickActionContext): SimpleAction[] {
  return SIMPLE_ACTIONS.filter((a) => ctx.can(a.access) && (a.visible?.(ctx) ?? true));
}

/**
 * The "N actions" figure for the result-count bar: role-visible simple actions
 * plus "Create API key". Role-only on purpose (the bar is mounted app-wide, the
 * per-workspace `visible` gates need the dialog-scoped hook); the two agree
 * because the JWT role IS the current workspace's role.
 */
export function quickActionsCount(can: QuickActionContext['can']): number {
  return SIMPLE_ACTIONS.filter((a) => can(a.access)).length + (can(ACCESS.apiKeyCreate) ? 1 : 0);
}
